import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
const serif = "'Georgia', 'Times New Roman', serif";
const gold = '#D4AF37';
const ivory = '#FAF3E0';

const FaceGallery = ({ token, activeFaceGroup, onSelectGroup }) => {
  const [groups, setGroups] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null); // {group_id, name, photo_ids, sample_crop, cover_photo_id}
  const [editMode, setEditMode] = useState(null); // null | 'rename' | 'cover' | 'confirmDelete'
  const [renameInput, setRenameInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [coffrePhotos, setCoffrePhotos] = useState([]);
  const [linkingId, setLinkingId] = useState(null);
  const [linkedPhotoIds, setLinkedPhotoIds] = useState([]); // ids fraîchement liés (pour feedback visuel)
  const headers = { Authorization: `Bearer ${token}` };
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const reload = async () => {
    try {
      const res = await axios.get(`${API}/faces/groups?status=coffre`, { headers });
      const named = (res.data.groups || []).filter(g => g.name && g.photo_count > 0);
      setGroups(named);
    } catch {}
  };

  useEffect(() => {
    if (!token) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startPress = (g) => {
    longPressFired.current = false;
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      // Vibration tactile (mobile) si supporté
      if (navigator.vibrate) try { navigator.vibrate(20); } catch {}
      setEditingGroup(g);
      setEditMode(null);
      setRenameInput(g.name || '');
    }, 500);
  };
  const cancelPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const handleClick = (g) => {
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    onSelectGroup(activeFaceGroup === g.group_id ? null : g.group_id);
  };

  const closeEdit = () => {
    setEditingGroup(null);
    setEditMode(null);
    setRenameInput('');
    setCoffrePhotos([]);
    setLinkedPhotoIds([]);
  };

  const openAddPhoto = async () => {
    setEditMode('addPhoto');
    if (coffrePhotos.length === 0) {
      try {
        const res = await axios.get(`${API}/photos?page=1&limit=100`, { headers });
        setCoffrePhotos(res.data.photos || []);
      } catch {
        toast.error('Impossible de charger le Coffre');
      }
    }
  };

  const doManualLink = async (photoId) => {
    if (!editingGroup) return;
    setLinkingId(photoId);
    try {
      const res = await axios.post(
        `${API}/faces/manual-link`,
        { photo_id: photoId, group_id: editingGroup.group_id },
        { headers }
      );
      if (res.data.status === 'already_linked') {
        toast(`Déjà dans ce cercle`, { style: { fontFamily: serif, backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: ivory } });
      } else {
        toast.success('Photo ajoutée au cercle', { style: { fontFamily: serif, backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: gold } });
      }
      setLinkedPhotoIds(prev => [...prev, photoId]);
    } catch {
      toast.error('Liaison impossible');
    }
    setLinkingId(null);
  };

  const doRename = async () => {
    const name = renameInput.trim();
    if (!name || !editingGroup) return;
    setBusy(true);
    try {
      await axios.post(`${API}/faces/name`, { group_id: editingGroup.group_id, name }, { headers });
      toast.success(`Cercle renommé en « ${name} »`, { style: { fontFamily: serif, backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: gold } });
      await reload();
      closeEdit();
    } catch {
      toast.error('Renommage impossible');
    }
    setBusy(false);
  };

  const doSetCover = async (photoId) => {
    if (!editingGroup) return;
    setBusy(true);
    try {
      await axios.post(`${API}/faces/set-cover`, { group_id: editingGroup.group_id, photo_id: photoId }, { headers });
      toast.success('Photo de couverture mise à jour', { style: { fontFamily: serif, backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: gold } });
      await reload();
      closeEdit();
    } catch {
      toast.error('Mise à jour impossible');
    }
    setBusy(false);
  };

  const doDelete = async () => {
    if (!editingGroup) return;
    setBusy(true);
    try {
      await axios.post(`${API}/faces/ignore`, { group_id: editingGroup.group_id }, { headers });
      toast.success(`Cercle « ${editingGroup.name} » supprimé`, { style: { fontFamily: serif, backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: gold } });
      // Si on filtrait sur ce groupe, on revient à "Tous"
      if (activeFaceGroup === editingGroup.group_id) onSelectGroup(null);
      await reload();
      closeEdit();
    } catch {
      toast.error('Suppression impossible');
    }
    setBusy(false);
  };

  if (groups.length === 0) return null;

  // URL de l'image du cercle : cover_photo_id > sample_crop > photo_ids[0] > initiale
  const getCircleImage = (g) => {
    if (g.cover_photo_id) return `${API}/photos/${g.cover_photo_id}/thumbnail?token=${token}`;
    if (g.sample_crop) return `data:image/jpeg;base64,${g.sample_crop}`;
    if (g.photo_ids && g.photo_ids[0]) return `${API}/photos/${g.photo_ids[0]}/thumbnail?token=${token}`;
    return null;
  };

  return (
    <div data-testid="face-gallery" style={{ marginBottom: 24 }}>
      <p style={{ fontFamily: serif, fontSize: 11, color: gold, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12, fontWeight: 400 }}>
        Retrouver
      </p>
      <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8, WebkitOverflowScrolling: 'touch' }}>
        {/* "Tous" button */}
        <button
          data-testid="face-gallery-all"
          onClick={() => onSelectGroup(null)}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            cursor: 'pointer', background: 'none', border: 'none', padding: 0, flexShrink: 0,
          }}
        >
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            border: !activeFaceGroup ? `2px solid ${gold}` : '1px solid rgba(212,175,55,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: !activeFaceGroup ? 'rgba(212,175,55,0.08)' : 'transparent',
            transition: 'all 0.2s',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="1.5" strokeLinecap="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
            </svg>
          </div>
          <span style={{
            fontFamily: serif, fontSize: 10, color: !activeFaceGroup ? gold : ivory,
            fontWeight: !activeFaceGroup ? 600 : 400, letterSpacing: 0.3, whiteSpace: 'nowrap',
          }}>Tous</span>
        </button>

        {groups.map(g => {
          const imgSrc = getCircleImage(g);
          return (
            <button
              key={g.group_id}
              data-testid={`face-gallery-${g.group_id}`}
              onClick={() => handleClick(g)}
              onPointerDown={() => startPress(g)}
              onPointerUp={cancelPress}
              onPointerLeave={cancelPress}
              onPointerCancel={cancelPress}
              onContextMenu={(e) => { e.preventDefault(); longPressFired.current = true; setEditingGroup(g); setEditMode(null); setRenameInput(g.name || ''); }}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0,
                cursor: 'pointer', background: 'none', border: 'none', padding: 0, WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
              }}
            >
              <div style={{
                width: 56, height: 56, borderRadius: '50%', overflow: 'hidden',
                border: activeFaceGroup === g.group_id ? `2px solid ${gold}` : '1px solid rgba(212,175,55,0.2)',
                transition: 'all 0.2s', flexShrink: 0,
              }}>
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt={g.name}
                    style={{ width: 56, height: 56, objectFit: 'cover', display: 'block' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(212,175,55,0.06)',
                  }}>
                    <span style={{ fontFamily: serif, fontSize: 18, color: gold }}>
                      {(g.name || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <span style={{
                fontFamily: serif, fontSize: 10,
                color: activeFaceGroup === g.group_id ? gold : ivory,
                fontWeight: activeFaceGroup === g.group_id ? 600 : 400,
                letterSpacing: 0.3, whiteSpace: 'nowrap', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {g.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Hint subtle (visible une seule fois côté UI, pas de surcharge) */}
      <p style={{ fontFamily: serif, fontSize: 10, color: ivory, opacity: 0.45, marginTop: 6, fontStyle: 'italic', textAlign: 'center' }}>
        Appui long sur un cercle pour le modifier
      </p>

      {/* ═══ MODALE D'ÉDITION DU CERCLE ═══ */}
      {editingGroup && (
        <div data-testid="face-edit-overlay"
          onClick={() => !busy && closeEdit()}
          style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0a0a0a', border: `1.5px solid rgba(212,175,55,0.3)`, borderRadius: 16,
              padding: '28px 24px', maxWidth: 460, width: '100%', maxHeight: '85vh', overflow: 'auto',
              boxShadow: '0 8px 60px rgba(0,0,0,0.7)',
            }}>
            <p style={{ fontFamily: serif, fontSize: 17, color: gold, textAlign: 'center', marginBottom: 4 }}>
              {editingGroup.name}
            </p>
            <p style={{ fontFamily: serif, fontSize: 12, color: ivory, textAlign: 'center', marginBottom: 22, opacity: 0.7 }}>
              {editingGroup.photo_count} photo{editingGroup.photo_count > 1 ? 's' : ''} dans ce cercle
            </p>

            {/* Menu principal */}
            {!editMode && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button data-testid="face-edit-rename"
                  onClick={() => setEditMode('rename')}
                  style={menuBtnStyle}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="1.8" strokeLinecap="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                  Renommer
                </button>
                <button data-testid="face-edit-add-photo"
                  onClick={openAddPhoto}
                  style={menuBtnStyle}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v8"/><path d="M8 12h8"/></svg>
                  Ajouter une photo
                </button>
                <button data-testid="face-edit-cover"
                  onClick={() => setEditMode('cover')}
                  style={menuBtnStyle}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                  Changer la photo de couverture
                </button>
                <button data-testid="face-edit-delete"
                  onClick={() => setEditMode('confirmDelete')}
                  style={{ ...menuBtnStyle, color: '#A8071A', borderColor: 'rgba(168,7,26,0.3)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A8071A" strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                  Supprimer ce cercle
                </button>
              </div>
            )}

            {/* Mode Rename */}
            {editMode === 'rename' && (
              <div data-testid="face-rename-form" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <input
                  data-testid="face-rename-input"
                  value={renameInput}
                  onChange={(e) => setRenameInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && renameInput.trim() && !busy) doRename(); }}
                  placeholder="Nouveau nom"
                  autoFocus
                  style={{
                    fontFamily: serif, fontSize: 15, padding: '12px 16px', borderRadius: 10,
                    border: `1.5px solid ${gold}`, background: 'rgba(0,0,0,0.5)', color: ivory,
                    outline: 'none', textAlign: 'center',
                  }} />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button data-testid="face-rename-confirm"
                    disabled={!renameInput.trim() || busy}
                    onClick={doRename}
                    style={{ ...primaryBtnStyle, flex: 1, opacity: (!renameInput.trim() || busy) ? 0.5 : 1 }}>
                    {busy ? '...' : 'Renommer'}
                  </button>
                  <button onClick={() => setEditMode(null)} style={secondaryBtnStyle} disabled={busy}>
                    Retour
                  </button>
                </div>
              </div>
            )}

            {/* Mode Cover */}
            {editMode === 'cover' && (
              <div data-testid="face-cover-form">
                <p style={{ fontFamily: serif, fontSize: 12, color: ivory, marginBottom: 12, opacity: 0.7, textAlign: 'center' }}>
                  Choisis la photo qui représentera ce cercle
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: 8, marginBottom: 14, maxHeight: '50vh', overflowY: 'auto' }}>
                  {(editingGroup.photo_ids || []).map((pid) => (
                    <button key={pid}
                      data-testid={`face-cover-pick-${pid}`}
                      disabled={busy}
                      onClick={() => doSetCover(pid)}
                      style={{
                        position: 'relative', width: '100%', aspectRatio: '1', borderRadius: 10,
                        overflow: 'hidden', cursor: 'pointer',
                        border: editingGroup.cover_photo_id === pid ? `2.5px solid ${gold}` : '1px solid rgba(212,175,55,0.2)',
                        background: '#000', padding: 0,
                        boxShadow: editingGroup.cover_photo_id === pid ? '0 0 14px rgba(212,175,55,0.45)' : 'none',
                        transition: 'all 0.2s',
                      }}>
                      <img src={`${API}/photos/${pid}/thumbnail?token=${token}`} alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </button>
                  ))}
                </div>
                <button onClick={() => setEditMode(null)} style={{ ...secondaryBtnStyle, width: '100%' }} disabled={busy}>
                  Retour
                </button>
              </div>
            )}

            {/* Mode AddPhoto - rattacher manuellement une photo du Coffre au cercle */}
            {editMode === 'addPhoto' && (
              <div data-testid="face-addphoto-form">
                <p style={{ fontFamily: serif, fontSize: 12, color: ivory, marginBottom: 12, opacity: 0.7, textAlign: 'center', lineHeight: 1.5 }}>
                  Touche les photos du Coffre o{'\u00f9'} appara{'\u00ee'}t <strong style={{ color: gold }}>{editingGroup.name}</strong>.<br />
                  Bobine les rattachera au cercle.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: 8, marginBottom: 14, maxHeight: '50vh', overflowY: 'auto' }}>
                  {coffrePhotos.length === 0 && (
                    <p style={{ gridColumn: '1/-1', fontFamily: serif, fontSize: 12, color: ivory, opacity: 0.6, textAlign: 'center', padding: 20 }}>
                      Chargement du Coffre…
                    </p>
                  )}
                  {coffrePhotos.map((p) => {
                    const alreadyIn = (editingGroup.photo_ids || []).includes(p.id);
                    const justLinked = linkedPhotoIds.includes(p.id);
                    const isLoading = linkingId === p.id;
                    const disabled = alreadyIn || justLinked || isLoading;
                    return (
                      <button key={p.id}
                        data-testid={`face-addphoto-pick-${p.id}`}
                        disabled={disabled}
                        onClick={() => doManualLink(p.id)}
                        title={alreadyIn ? 'Déjà dans ce cercle' : justLinked ? 'Ajoutée' : 'Toucher pour ajouter'}
                        style={{
                          position: 'relative', width: '100%', aspectRatio: '1', borderRadius: 10,
                          overflow: 'hidden', cursor: disabled ? 'default' : 'pointer',
                          border: justLinked ? `2.5px solid ${gold}` : '1px solid rgba(212,175,55,0.2)',
                          background: '#000', padding: 0,
                          opacity: alreadyIn ? 0.35 : 1,
                          boxShadow: justLinked ? '0 0 14px rgba(212,175,55,0.45)' : 'none',
                          transition: 'all 0.2s',
                        }}>
                        <img src={`${API}/photos/${p.id}/thumbnail?token=${token}`} alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        {alreadyIn && (
                          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                          </div>
                        )}
                        {justLinked && !alreadyIn && (
                          <div style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: gold, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                          </div>
                        )}
                        {isLoading && (
                          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid rgba(212,175,55,0.3)`, borderTopColor: gold, animation: 'spin 0.8s linear infinite' }} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => { setEditMode(null); if (linkedPhotoIds.length > 0) reload(); }} style={{ ...secondaryBtnStyle, width: '100%' }}>
                  {linkedPhotoIds.length > 0 ? `Retour (${linkedPhotoIds.length} ajout${linkedPhotoIds.length > 1 ? 's' : ''})` : 'Retour'}
                </button>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {/* Mode confirm Delete */}
            {editMode === 'confirmDelete' && (
              <div data-testid="face-delete-confirm">
                <p style={{ fontFamily: serif, fontSize: 14, color: ivory, textAlign: 'center', marginBottom: 8, lineHeight: 1.6 }}>
                  Supprimer le cercle <strong style={{ color: gold }}>« {editingGroup.name} »</strong> ?
                </p>
                <p style={{ fontFamily: serif, fontSize: 11, color: ivory, opacity: 0.6, textAlign: 'center', marginBottom: 18, fontStyle: 'italic', lineHeight: 1.5 }}>
                  Les photos restent dans ton Coffre, seul le cercle disparaît de "Retrouver".
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button data-testid="face-delete-confirm-btn"
                    disabled={busy}
                    onClick={doDelete}
                    style={{
                      flex: 1, fontFamily: serif, fontSize: 13, fontWeight: 600,
                      padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                      background: '#A8071A', color: '#fff', border: 'none',
                      opacity: busy ? 0.6 : 1,
                    }}>
                    {busy ? '...' : 'Oui, supprimer'}
                  </button>
                  <button onClick={() => setEditMode(null)} style={secondaryBtnStyle} disabled={busy}>
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Bouton Fermer global (uniquement en mode menu) */}
            {!editMode && (
              <button onClick={closeEdit}
                style={{
                  marginTop: 18, width: '100%', fontFamily: serif, fontSize: 12,
                  padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                  background: 'transparent', color: ivory, border: '1px solid rgba(212,175,55,0.2)',
                }}>
                Fermer
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const menuBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 12,
  fontFamily: serif, fontSize: 14, padding: '13px 18px',
  borderRadius: 10, cursor: 'pointer',
  background: 'rgba(212,175,55,0.04)', color: ivory,
  border: '1px solid rgba(212,175,55,0.25)', textAlign: 'left',
  transition: 'all 0.2s',
};

const primaryBtnStyle = {
  fontFamily: serif, fontSize: 14, fontWeight: 600, padding: '12px 24px',
  borderRadius: 10, cursor: 'pointer',
  background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000',
  border: 'none', transition: 'all 0.25s',
};

const secondaryBtnStyle = {
  fontFamily: serif, fontSize: 13, padding: '11px 20px', borderRadius: 10,
  cursor: 'pointer', background: 'transparent', color: gold,
  border: '1px solid rgba(212,175,55,0.3)',
};

export default FaceGallery;
